import type { QueryClient, Query } from '@tanstack/react-query';
import type { ScreenplayWorkflowSnapshot } from '@/types/ai-session';
import { canvasCustomGroupsKeys } from '@/hooks/api/useCanvasCustomGroups';
import { invalidateCanvasReferenceableQueriesForStoryboard } from '@/hooks/api/useCanvasReferenceableResources';
import { canvasLayoutKeys } from '@/hooks/api/useCanvasLayout';
import {
  canvasSnapshotKeys,
  invalidateCanvasHistoryQueriesForAsset,
} from '@/hooks/api/useCanvasSnapshot';
import { invalidateMentionQueriesForStoryboard } from '@/hooks/api/useMentionableResources';
import { applyGenerationReferenceDraftChanged } from '@/hooks/api/useGenerationReferenceDraft';
import { invalidateAIConversationMessages } from '@/hooks/api/useAIConversations';
import { elementDetailKeys } from '@/hooks/api/useProjectElementsDetail';
import { invalidateEpisodeLinkedResourceQueries } from '@/hooks/api/useResourceLibrary';
import { folderKeys } from '@/hooks/api/useFolders';
import { orgMembersKeys } from '@/hooks/api/useOrgMembers';
import { projectKeys } from '@/hooks/api/useProjects';
import { collaboratorOrgKeys } from '@/hooks/api/useProjectCollaboratorOrgs';
import { resolveRouteHighlightContext } from '@/hooks/use-collaboration-highlights';
import {
  mapCanvasEntitiesChangedToHighlights,
  normalizeCanvasEntityChangedItemType,
} from '@/lib/collaboration/canvas-entity-highlight';
import {
  applyOrgAccessChangedEffects,
  applyProjectAccessRevokedEffects,
  applyProjectAuthChangedEffects,
} from '@/lib/notification-side-effects';
import {
  isOwnCanvasConversationChange,
  removeCanvasSnapshotConversationById,
  upsertCanvasSnapshotConversation,
} from '@/lib/canvas/canvas-conversation-hub-sync';
import {
  getCanvasLayoutItemIdentityKey,
  isPersistentCanvasLayoutItemType,
} from '@/lib/canvas/canvas-layout-item-key';
import { invalidateContextDependentQueries } from '@/lib/invalidate-context-dependent-queries';
import { mapStoryboardDialogueApiItem } from '@/lib/dialogue-utils';
import {
  applyProjectElementDeletedCacheSync,
  applyProjectResourceDeletedCacheSync,
  applyProjectResourceUpdatedCacheSync,
} from '@/lib/realtime/project-resource-cache-sync';
import { applyStoryboardDialogueDeletedCacheSync } from '@/lib/realtime/storyboard-dialogue-cache-sync';
import {
  applyStoryboardSoundEffectDeletedCacheSync,
  upsertStoryboardSoundEffectCache,
} from '@/lib/realtime/storyboard-sound-effect-cache-sync';
import {
  applyStoryboardVoiceoverDeletedCacheSync,
  upsertStoryboardVoiceoverCache,
} from '@/lib/realtime/storyboard-voiceover-cache-sync';
import {
  applyStoryboardVideoDeletedCacheSync,
  refreshStoryboardVideoCollaborationCache,
} from '@/lib/realtime/storyboard-video-cache-sync';
import {
  applyTagSelectedResourceChangedCacheSync,
  mapElementTypeToAssetType,
} from '@/lib/realtime/tag-selected-resource-cache-sync';
import { isRealtimeCollaborationProject } from '@/lib/realtime-collaboration-project';
import { storyboardResourcesApi } from '@/services/api/storyboard-resources';
import type {
  DialogueVoiceoverResponse,
  StoryboardSoundEffectResponse,
} from '@/services/api/storyboard-resources';
import { useAppStore } from '@/stores/appStore';
import { useCanvasAssetAssociationAttentionStore } from '@/stores/canvasAssetAssociationAttentionStore';
import { useCollaborationHighlightStore } from '@/stores/collaborationHighlightStore';
import type { CanvasAssetType, CanvasContextType, CanvasLayoutResponse } from '@/types/canvas';
import type { CanvasConversationSummary } from '@/types/canvas-snapshot';
import type {
  GenerationReferenceDraft,
  GenerationReferenceHumanAsset,
  GenerationReferenceItem,
  GenerationTargetKey,
} from '@/types/generation-reference';
import type {
  CanvasEntitiesChangedPayload,
  CanvasEntityChangeOperation,
  CanvasEntityChangedNode,
  RealtimeContext,
  RealtimeEvent,
} from '@/types/realtime';

const CACHE_BUCKET_DEBOUNCE_MS = 500;

type ProjectCachePayload = {
  orgId?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  affectedProjectIds?: string[];
  tagId?: string | null;
  screenplayId?: string | null;
  screenplayIds?: string[];
  storyboardId?: string | null;
  storyboardIds?: string[];
  reviewId?: string | null;
  reviewIds?: string[];
  parentProjectId?: string | null;
  previousParentProjectId?: string | null;
  resourceId?: string | null;
  resourceType?: string | null;
  resourceName?: string | null;
  selectedResourceId?: string | null;
  tagImageUrl?: string | null;
  pcoId?: string | null;
  collabOrgId?: string | null;
  elementId?: string | null;
  elementType?: string | null;
  entityId?: string | null;
  entityType?: string | null;
  changeType?: string | null;
  imageEditHistoryOnly?: boolean;
  data?: Record<string, unknown> | null;
};

type OrgCachePayload = {
  orgId?: string | null;
  orgName?: string | null;
  targetUserId?: string | null;
};

type PendingBucket = {
  timer: ReturnType<typeof setTimeout>;
  run: () => void;
  reasons: Set<string>;
  payload?: ProjectCachePayload;
};

type PendingCanvasEntitiesBucket = {
  timer: ReturnType<typeof setTimeout>;
  run: () => void;
  reasons: Set<string>;
  payload: CanvasEntitiesChangedPayload;
};

const pendingBuckets = new Map<string, PendingBucket>();
const pendingCanvasEntityBuckets = new Map<string, PendingCanvasEntitiesBucket>();
let pendingResourceAndTagPayload: ProjectCachePayload | null = null;

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
}

function isCanvasContextType(value: string | null): value is CanvasContextType {
  return value === 'storyboard' || value === 'asset';
}

function isCanvasAssetType(value: string | null): value is CanvasAssetType {
  return value === 'character' || value === 'location' || value === 'prop';
}

function isCanvasEntityChangeOperation(value: string | null): value is CanvasEntityChangeOperation {
  return value === 'created' || value === 'updated' || value === 'deleted' || value === 'replaced';
}

function readCanvasEntityChangedNodes(value: unknown): CanvasEntityChangedNode[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const raw = item as Record<string, unknown>;
    const operation = readString(raw.operation);
    const itemType = normalizeCanvasEntityChangedItemType(raw.itemType ?? raw.item_type);
    const itemId = readString(raw.itemId) ?? readString(raw.item_id);
    if (!isCanvasEntityChangeOperation(operation) || !itemType || !itemId) return [];
    const node: CanvasEntityChangedNode = {
      operation,
      itemType,
      itemId,
    };
    const previousItemId = readString(raw.previousItemId);
    const entityType = readString(raw.entityType);
    const entityId = readString(raw.entityId);
    if (previousItemId) node.previousItemId = previousItemId;
    if (entityType) node.entityType = entityType;
    if (entityId) node.entityId = entityId;
    if (raw.metaData && typeof raw.metaData === 'object' && !Array.isArray(raw.metaData)) {
      node.metaData = raw.metaData as Record<string, unknown>;
    }
    return [node];
  });
}

function readCanvasEntitiesChangedPayload(
  payload: Record<string, unknown>
): CanvasEntitiesChangedPayload | null {
  const projectId = readString(payload.projectId);
  const contextType = readString(payload.contextType);
  if (!projectId || !isCanvasContextType(contextType)) return null;

  const assetType = readString(payload.assetType);
  return {
    projectId,
    affectedProjectIds: readStringArray(payload.affectedProjectIds),
    contextType,
    storyboardId: readString(payload.storyboardId),
    assetType: isCanvasAssetType(assetType) ? assetType : null,
    assetId: readString(payload.assetId),
    changedNodes: readCanvasEntityChangedNodes(payload.changedNodes),
  };
}

/** 逐项校验：消费端直接读属性，数组里混进 null 会让生成框整块渲染失败 */
function readGenerationReferenceHumanAssets(value: unknown): GenerationReferenceHumanAsset[] {
  if (!Array.isArray(value)) return [];
  const humanAssets: GenerationReferenceHumanAsset[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const raw = item as Record<string, unknown>;
    if (typeof raw.resourceType !== 'number' || typeof raw.resourceId !== 'string') continue;
    humanAssets.push(raw as unknown as GenerationReferenceHumanAsset);
  }
  return humanAssets;
}

function readGenerationReferenceDraftChangedPayload(
  payload: Record<string, unknown>
): GenerationReferenceDraft | null {
  const projectId = readString(payload.projectId);
  const target = payload.target;
  const references = payload.references;
  if (!projectId || !target || typeof target !== 'object' || !Array.isArray(references)) {
    return null;
  }
  const rawTarget = target as Record<string, unknown>;
  if (
    rawTarget.projectId !== projectId ||
    typeof rawTarget.targetId !== 'string' ||
    typeof rawTarget.targetType !== 'number' ||
    typeof rawTarget.mediaType !== 'number'
  ) {
    return null;
  }
  const updatedAt = readString(payload.updatedAt);
  const updatedBy = readString(payload.updatedBy);
  return {
    target: rawTarget as unknown as GenerationTargetKey,
    references: references as GenerationReferenceItem[],
    // 始终写入：草稿是整包覆盖，缺字段时若跳过会把缓存里已有的认证标识静默抹掉
    humanAssets: readGenerationReferenceHumanAssets(payload.humanAssets),
    ...(updatedAt ? { updatedAt } : {}),
    ...(updatedBy ? { updatedBy } : {}),
  };
}

/** 协作者 / 定稿迁移：就地写 draft query + snapshot.drafts（含 resource_replaced）。 */
export { applyGenerationReferenceDraftChanged };

function readCanvasConversationChangedPayload(
  payload: Record<string, unknown>
): { projectId: string; conversation: CanvasConversationSummary } | null {
  const projectId = readString(payload.projectId);
  const conversation = payload.conversation;
  if (!projectId || !conversation || typeof conversation !== 'object') return null;
  const value = conversation as Record<string, unknown>;
  const target = value.target;
  if (
    typeof value.conversationId !== 'string' ||
    typeof value.title !== 'string' ||
    typeof value.messageCount !== 'number' ||
    !target ||
    typeof target !== 'object'
  ) {
    return null;
  }

  const firstMessages: CanvasConversationSummary['firstMessages'] = [];
  const message = value.message;
  if (message && typeof message === 'object') {
    const firstMessage = message as Record<string, unknown>;
    if (
      typeof firstMessage.messageId !== 'string' ||
      typeof firstMessage.role !== 'number' ||
      typeof firstMessage.content !== 'string' ||
      typeof firstMessage.orderIndex !== 'number' ||
      !firstMessage.metaData ||
      typeof firstMessage.metaData !== 'object' ||
      typeof firstMessage.createdAt !== 'string'
    ) {
      return null;
    }
    firstMessages.push({
      messageId: firstMessage.messageId,
      role: firstMessage.role,
      content: firstMessage.content,
      orderIndex: firstMessage.orderIndex,
      metaData: firstMessage.metaData as Record<string, unknown>,
      createdAt: firstMessage.createdAt,
    });
  }

  return {
    projectId,
    conversation: {
      conversationId: value.conversationId,
      userId: typeof value.userId === 'string' ? value.userId : null,
      target: target as unknown as GenerationTargetKey,
      title: value.title,
      messageCount: value.messageCount,
      lastMessageAt: typeof value.lastMessageAt === 'string' ? value.lastMessageAt : null,
      firstMessages,
    },
  };
}

function applyCanvasConversationChanged(
  queryClient: QueryClient,
  payload: { projectId: string; conversation: CanvasConversationSummary }
): void {
  upsertCanvasSnapshotConversation(queryClient, payload);
  // hub 面板用 messages query；仅 upsert snapshot 不会刷新消息列表。
  invalidateAIConversationMessages(queryClient, payload.conversation.conversationId);
}

function readProjectCachePayload(payload: Record<string, unknown>): ProjectCachePayload {
  return {
    orgId: readString(payload.orgId),
    projectId: readString(payload.projectId),
    projectName: readString(payload.projectName),
    affectedProjectIds: readStringArray(payload.affectedProjectIds),
    tagId: readString(payload.tagId),
    screenplayId: readString(payload.screenplayId),
    storyboardId: readString(payload.storyboardId),
    reviewId: readString(payload.reviewId),
    parentProjectId: readString(payload.parentProjectId),
    previousParentProjectId: readString(payload.previousParentProjectId),
    resourceId: readString(payload.resourceId),
    resourceType: readString(payload.resourceType),
    resourceName: readString(payload.name),
    selectedResourceId: readString(payload.selectedResourceId),
    tagImageUrl: readString(payload.tagImageUrl),
    pcoId: readString(payload.pcoId),
    collabOrgId: readString(payload.collabOrgId),
    elementId: readString(payload.elementId),
    elementType: readString(payload.elementType),
    entityId: readString(payload.entityId),
    entityType: readString(payload.entityType),
    changeType: readString(payload.changeType),
    imageEditHistoryOnly: payload.imageEditHistoryOnly === true,
    data:
      payload.data && typeof payload.data === 'object'
        ? (payload.data as Record<string, unknown>)
        : null,
  };
}

function readOrgCachePayload(payload: Record<string, unknown>): OrgCachePayload {
  return {
    orgId: readString(payload.orgId),
    orgName: readString(payload.orgName),
    targetUserId: readString(payload.targetUserId),
  };
}

function shouldApplyOrgAccessChanged(payload: OrgCachePayload): boolean {
  if (!payload.targetUserId) return true;
  const currentUserId = useAppStore.getState().user?.userId;
  return !currentUserId || payload.targetUserId === currentUserId;
}

function scheduleCacheBucket(bucket: string, reason: string, run: () => void): void {
  const existing = pendingBuckets.get(bucket);
  if (existing) {
    existing.reasons.add(reason);
    return;
  }

  const pending: PendingBucket = {
    timer: setTimeout(() => {
      pendingBuckets.delete(bucket);
      run();
    }, CACHE_BUCKET_DEBOUNCE_MS),
    run,
    reasons: new Set([reason]),
  };
  pendingBuckets.set(bucket, pending);
}

/** 协作成果通知保留一个尾随刷新，避免慢请求被后续通知持续取消。 */
function scheduleWorkflowCacheRefresh(
  queryClient: QueryClient,
  bucket: string,
  queryKeys: readonly (readonly unknown[])[]
): void {
  const refresh = () => {
    const reading = queryKeys.some((queryKey) =>
      queryClient
        .getQueryCache()
        .findAll({ queryKey })
        .some((query) => query.state.fetchStatus === 'fetching')
    );
    if (reading) {
      scheduleCacheBucket(bucket, 'waiting_for_read', refresh);
      return;
    }
    for (const queryKey of queryKeys) {
      void queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false });
    }
  };
  scheduleCacheBucket(bucket, 'screenplay_workflow_changed', refresh);
}

function mergeStringScope(current?: string | null, next?: string | null): string | null {
  if (!current) return next ?? null;
  if (!next || current === next) return current;
  return null;
}

function collectStringScopeValues(
  current?: string | null,
  currentValues?: string[],
  next?: string | null,
  nextValues?: string[]
): string[] {
  return Array.from(
    new Set(
      [current, ...(currentValues ?? []), next, ...(nextValues ?? [])].filter(
        (value): value is string => Boolean(value)
      )
    )
  );
}

function mergePreciseStringScope(
  current: ProjectCachePayload,
  next: ProjectCachePayload,
  idKey: 'screenplayId' | 'storyboardId' | 'reviewId',
  idsKey: 'screenplayIds' | 'storyboardIds' | 'reviewIds'
): void {
  const values = collectStringScopeValues(
    current[idKey],
    current[idsKey],
    next[idKey],
    next[idsKey]
  );
  current[idKey] = values.length === 1 ? values[0] : null;
  current[idsKey] = values.length > 1 ? values : undefined;
}

function getPreciseStringScopeValues(
  payload: ProjectCachePayload,
  idKey: 'screenplayId' | 'storyboardId' | 'reviewId',
  idsKey: 'screenplayIds' | 'storyboardIds' | 'reviewIds'
): string[] {
  return collectStringScopeValues(payload[idKey], payload[idsKey]);
}

function cloneProjectCachePayload(payload: ProjectCachePayload): ProjectCachePayload {
  return {
    ...payload,
    affectedProjectIds: [...(payload.affectedProjectIds ?? [])],
    screenplayIds: payload.screenplayIds ? [...payload.screenplayIds] : undefined,
    storyboardIds: payload.storyboardIds ? [...payload.storyboardIds] : undefined,
    reviewIds: payload.reviewIds ? [...payload.reviewIds] : undefined,
  };
}

function mergeProjectCachePayload(
  current: ProjectCachePayload,
  next: ProjectCachePayload
): ProjectCachePayload {
  const affectedProjectIds = new Set([
    ...(current.affectedProjectIds ?? []),
    ...(next.affectedProjectIds ?? []),
    ...(current.projectId ? [current.projectId] : []),
    ...(next.projectId ? [next.projectId] : []),
  ]);
  current.orgId = mergeStringScope(current.orgId, next.orgId);
  current.projectId = mergeStringScope(current.projectId, next.projectId);
  current.projectName = mergeStringScope(current.projectName, next.projectName);
  current.tagId = mergeStringScope(current.tagId, next.tagId);
  mergePreciseStringScope(current, next, 'screenplayId', 'screenplayIds');
  mergePreciseStringScope(current, next, 'storyboardId', 'storyboardIds');
  mergePreciseStringScope(current, next, 'reviewId', 'reviewIds');
  current.parentProjectId = mergeStringScope(current.parentProjectId, next.parentProjectId);
  current.previousParentProjectId = mergeStringScope(
    current.previousParentProjectId,
    next.previousParentProjectId
  );
  current.changeType = next.changeType || current.changeType;
  current.pcoId = next.pcoId || current.pcoId;
  current.collabOrgId = next.collabOrgId || current.collabOrgId;
  current.affectedProjectIds = [...affectedProjectIds];
  return current;
}

function scheduleProjectCacheBucket(
  bucket: string,
  reason: string,
  payload: ProjectCachePayload,
  run: (payload: ProjectCachePayload) => void
): void {
  const existing = pendingBuckets.get(bucket);
  if (existing) {
    existing.reasons.add(reason);
    if (existing.payload) {
      mergeProjectCachePayload(existing.payload, payload);
    }
    return;
  }

  const mergedPayload = cloneProjectCachePayload(payload);
  const pending = {
    timer: setTimeout(() => {
      pendingBuckets.delete(bucket);
      run(mergedPayload);
    }, CACHE_BUCKET_DEBOUNCE_MS),
    run: () => run(mergedPayload),
    reasons: new Set([reason]),
    payload: mergedPayload,
  };
  pendingBuckets.set(bucket, pending);
}

function cloneCanvasEntitiesChangedPayload(
  payload: CanvasEntitiesChangedPayload
): CanvasEntitiesChangedPayload {
  return {
    projectId: payload.projectId,
    affectedProjectIds: [...(payload.affectedProjectIds ?? [])],
    contextType: payload.contextType,
    storyboardId: payload.storyboardId,
    assetType: payload.assetType,
    assetId: payload.assetId,
    changedNodes: payload.changedNodes.map((node) => ({ ...node })),
  };
}

function mergeCanvasEntitiesChangedPayload(
  current: CanvasEntitiesChangedPayload,
  next: CanvasEntitiesChangedPayload
): CanvasEntitiesChangedPayload {
  const affectedProjectIds = new Set([
    ...(current.affectedProjectIds ?? []),
    ...(next.affectedProjectIds ?? []),
    current.projectId,
    next.projectId,
  ]);
  current.affectedProjectIds = [...affectedProjectIds];
  current.changedNodes = [...current.changedNodes, ...next.changedNodes];
  return current;
}

function scheduleCanvasEntitiesChangedBucket(
  bucket: string,
  reason: string,
  payload: CanvasEntitiesChangedPayload,
  run: (payload: CanvasEntitiesChangedPayload) => void
): void {
  const existing = pendingCanvasEntityBuckets.get(bucket);
  if (existing) {
    existing.reasons.add(reason);
    mergeCanvasEntitiesChangedPayload(existing.payload, payload);
    return;
  }

  const mergedPayload = cloneCanvasEntitiesChangedPayload(payload);
  const pending: PendingCanvasEntitiesBucket = {
    timer: setTimeout(() => {
      pendingCanvasEntityBuckets.delete(bucket);
      run(mergedPayload);
    }, CACHE_BUCKET_DEBOUNCE_MS),
    run: () => run(mergedPayload),
    reasons: new Set([reason]),
    payload: mergedPayload,
  };
  pendingCanvasEntityBuckets.set(bucket, pending);
}

function flushCacheBuckets(bucketPrefix?: string): void {
  // 只处理本轮快照；回调重新入队的任务留给定时器，避免 Map 活迭代同步循环。
  const cacheBuckets = [...pendingBuckets.entries()];
  const canvasBuckets = [...pendingCanvasEntityBuckets.entries()];
  for (const [bucket, pending] of cacheBuckets) {
    if (bucketPrefix && !bucket.startsWith(bucketPrefix)) continue;
    clearTimeout(pending.timer);
    pendingBuckets.delete(bucket);
    pending.run();
  }
  for (const [bucket, pending] of canvasBuckets) {
    if (bucketPrefix && !bucket.startsWith(bucketPrefix)) continue;
    clearTimeout(pending.timer);
    pendingCanvasEntityBuckets.delete(bucket);
    pending.run();
  }
}

function invalidateProjectBudgetChangedQueries(
  queryClient: QueryClient,
  payload: ProjectCachePayload
): void {
  void queryClient.invalidateQueries({ queryKey: ['org'] });
  const orgId = payload.orgId;
  void queryClient.invalidateQueries({ queryKey: ['projects', 'list'] });
  void queryClient.invalidateQueries({ queryKey: ['projects', 'detail'] });
  void queryClient.invalidateQueries({ queryKey: ['folders', 'tree'] });
  void queryClient.invalidateQueries({ queryKey: ['context-resource-summary'] });

  if (orgId) {
    void queryClient.invalidateQueries({ queryKey: ['org', orgId, 'credits'] });
    void queryClient.invalidateQueries({ queryKey: ['org', orgId, 'reports'] });
    void queryClient.invalidateQueries({
      queryKey: ['org-project-budget-tree', orgId],
      exact: false,
    });
  } else {
    void queryClient.invalidateQueries({ queryKey: ['org-project-budget-tree'] });
  }
}

function getPayloadProjectIds(payload: ProjectCachePayload): string[] {
  return payload.affectedProjectIds?.length
    ? payload.affectedProjectIds
    : [payload.projectId].filter((projectId): projectId is string => Boolean(projectId));
}

function mergeResourceAndTagPayload(
  current: ProjectCachePayload | null,
  next: ProjectCachePayload
): ProjectCachePayload {
  if (!current) {
    return {
      ...next,
      affectedProjectIds: getPayloadProjectIds(next),
    };
  }

  const affectedProjectIds = Array.from(
    new Set([...getPayloadProjectIds(current), ...getPayloadProjectIds(next)])
  );
  const tagId = current.tagId && next.tagId && current.tagId === next.tagId ? current.tagId : null;

  return {
    ...current,
    affectedProjectIds,
    tagId,
  };
}

function invalidateProjectOrgCollaborationQueries(
  queryClient: QueryClient,
  payload: ProjectCachePayload
): void {
  void queryClient.invalidateQueries({
    queryKey: ['org-project-budget-tree'],
    exact: false,
  });
  void queryClient.invalidateQueries({ queryKey: collaboratorOrgKeys.all });
  void queryClient.invalidateQueries({ queryKey: ['projects', 'subproject-member-management'] });
  if (payload.projectId) {
    void queryClient.invalidateQueries({
      queryKey: collaboratorOrgKeys.byProjectRoot(payload.projectId),
    });
    if (payload.pcoId) {
      void queryClient.invalidateQueries({
        queryKey: collaboratorOrgKeys.memberCandidatesRoot(payload.projectId, payload.pcoId),
      });
    }
  }
  if (payload.changeType === 'terminated' || payload.changeType === 'revoked') {
    void queryClient.invalidateQueries({ queryKey: folderKeys.trees() });
    void queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
  }
}

function invalidateProjectResourceAndTagQueries(
  queryClient: QueryClient,
  payload: ProjectCachePayload
): void {
  const projectIds = getPayloadProjectIds(payload);

  void queryClient.invalidateQueries({ queryKey: ['resource-library', 'v1'] });
  void queryClient.invalidateQueries({ queryKey: ['project-element-tags'] });
  void queryClient.invalidateQueries({ queryKey: elementDetailKeys.all });
  void queryClient.invalidateQueries({ queryKey: ['project-elements'] });
  void queryClient.invalidateQueries({ queryKey: ['ai-mentions'] });
  void queryClient.invalidateQueries({ queryKey: ['storyboard-items'] });
  void queryClient.invalidateQueries({ queryKey: ['storyboard-board'] });
  void queryClient.invalidateQueries({ queryKey: ['storyboards'] });

  for (const projectId of projectIds) {
    void queryClient.invalidateQueries({ queryKey: ['project-elements-for-picker', projectId] });
    if (payload.tagId) {
      void queryClient.invalidateQueries({
        queryKey: ['resource-library', 'v1', 'tag-resources', projectId, payload.tagId],
      });
    }
  }
}

function invalidateProjectStateQueries(
  queryClient: QueryClient,
  payload: ProjectCachePayload
): void {
  void queryClient.invalidateQueries({ queryKey: ['projects', 'list'] });
  void queryClient.invalidateQueries({ queryKey: ['projects', 'detail'] });
  void queryClient.invalidateQueries({ queryKey: ['projects', 'trash'] });
  void queryClient.invalidateQueries({ queryKey: ['folders', 'tree'] });
  void queryClient.invalidateQueries({ queryKey: ['context-resource-summary'] });
  if (payload.orgId) {
    void queryClient.invalidateQueries({
      queryKey: ['org-project-budget-tree', payload.orgId],
      exact: false,
    });
  } else {
    void queryClient.invalidateQueries({ queryKey: ['org-project-budget-tree'] });
  }

  const parentProjectIds = new Set(
    [payload.parentProjectId, payload.previousParentProjectId].filter((value): value is string =>
      Boolean(value)
    )
  );
  if (parentProjectIds.size > 0) {
    for (const parentProjectId of parentProjectIds) {
      void queryClient.invalidateQueries({
        queryKey: ['projects', 'subprojects', parentProjectId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['projects', 'subproject-member-targets', parentProjectId],
      });
    }
  } else {
    void queryClient.invalidateQueries({ queryKey: ['projects', 'subprojects'] });
    void queryClient.invalidateQueries({ queryKey: ['projects', 'subproject-member-targets'] });
  }
}

function invalidateProjectStructureQueries(
  queryClient: QueryClient,
  payload: ProjectCachePayload
): void {
  invalidateProjectStateQueries(queryClient, payload);
  void queryClient.invalidateQueries({ queryKey: ['projects', 'subproject-member-management'] });
}

const STORYBOARD_ITEM_ASSOCIATION_CHANGE_TYPES = new Set([
  'item_added',
  'item_removed',
  'item_updated',
  'items_reordered',
]);

function invalidateStoryboardItemAssociationQueries(
  queryClient: QueryClient,
  payload: ProjectCachePayload
): void {
  void invalidateEpisodeLinkedResourceQueries(queryClient);
  const storyboardId = payload.storyboardId?.trim();
  if (storyboardId) {
    void queryClient.invalidateQueries({ queryKey: ['storyboard-items', storyboardId] });
    invalidateMentionQueriesForStoryboard(queryClient, storyboardId);
    invalidateCanvasReferenceableQueriesForStoryboard(queryClient, storyboardId);
  } else {
    void queryClient.invalidateQueries({ queryKey: ['storyboard-items'] });
  }
  void queryClient.invalidateQueries({ queryKey: ['storyboardBoard'] });
}

function invalidateProjectProductionQueries(
  queryClient: QueryClient,
  payload: ProjectCachePayload
): void {
  const projectIds = new Set(
    [payload.projectId, ...(payload.affectedProjectIds ?? [])].filter((value): value is string =>
      Boolean(value)
    )
  );

  void queryClient.invalidateQueries({ queryKey: ['screenplays', 'list'] });
  void queryClient.invalidateQueries({ queryKey: ['screenplays', 'dialog-directory'] });
  void queryClient.invalidateQueries({ queryKey: ['storyboards'] });
  void queryClient.invalidateQueries({ queryKey: ['storyboardBoard'] });
  void queryClient.invalidateQueries({ queryKey: ['storyboard-reviews'] });
  void queryClient.invalidateQueries({ queryKey: ['storyboard-items'] });
  void queryClient.invalidateQueries({ queryKey: ['storyboard-images'] });
  void queryClient.invalidateQueries({ queryKey: ['storyboard-plans'] });
  void queryClient.invalidateQueries({ queryKey: ['storyboard-videos'] });
  void queryClient.invalidateQueries({ queryKey: ['storyboardDialogues'] });
  void queryClient.invalidateQueries({ queryKey: ['dialogueVoiceovers'] });
  void queryClient.invalidateQueries({ queryKey: ['storyboard-sound-effects'] });
  void queryClient.invalidateQueries({ queryKey: ['ai-mentions'] });
  void queryClient.invalidateQueries({ queryKey: ['task-events'] });

  for (const projectId of projectIds) {
    void queryClient.invalidateQueries({ queryKey: ['screenplays', 'list', projectId] });
    void queryClient.invalidateQueries({
      queryKey: ['screenplays', 'dialog-directory', projectId],
    });
    void queryClient.invalidateQueries({ queryKey: ['storyboards', 'list', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['storyboardBoard', 'list', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['storyboardBoard', 'skeleton', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['storyboardBoard', 'paginated', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['storyboard-reviews', 'status', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['projects', 'detail'] });
  }

  for (const screenplayId of getPreciseStringScopeValues(
    payload,
    'screenplayId',
    'screenplayIds'
  )) {
    void queryClient.invalidateQueries({
      queryKey: ['screenplays', 'detail', screenplayId],
    });
    void queryClient.invalidateQueries({
      queryKey: ['screenplays', 'parse-status', screenplayId],
    });
    void queryClient.invalidateQueries({
      queryKey: ['screenplays', 'scenes', screenplayId],
    });
  }

  for (const storyboardId of getPreciseStringScopeValues(
    payload,
    'storyboardId',
    'storyboardIds'
  )) {
    void queryClient.invalidateQueries({
      queryKey: ['storyboards', 'detail', storyboardId],
    });
    void queryClient.invalidateQueries({
      queryKey: ['storyboards', 'scene', storyboardId],
    });
    void queryClient.invalidateQueries({ queryKey: ['storyboard-items', storyboardId] });
    void queryClient.refetchQueries({
      queryKey: ['storyboard-items', storyboardId],
      type: 'active',
    });
    void queryClient.invalidateQueries({ queryKey: ['storyboard-images', storyboardId] });
    void queryClient.invalidateQueries({
      queryKey: ['storyboard-plans', 'storyboard', storyboardId],
    });
    void queryClient.invalidateQueries({ queryKey: ['storyboard-videos', storyboardId] });
    void queryClient.invalidateQueries({ queryKey: ['storyboardDialogues', storyboardId] });
    void queryClient.invalidateQueries({
      queryKey: ['storyboard-sound-effects', storyboardId],
    });
  }

  for (const reviewId of getPreciseStringScopeValues(payload, 'reviewId', 'reviewIds')) {
    void queryClient.invalidateQueries({
      queryKey: ['storyboard-reviews', 'diff', reviewId],
    });
  }
}

type StoryboardItemCacheEntry = {
  itemId?: string;
  item_id?: string;
};

function applyStoryboardItemDeletedCacheSync(
  queryClient: QueryClient,
  payload: CanvasEntitiesChangedPayload
): void {
  if (payload.contextType !== 'storyboard' || !payload.storyboardId) return;
  const deletedItemIds = payload.changedNodes
    .filter((node) => node.operation === 'deleted' && node.itemType === 'storyboard_item')
    .map((node) => node.itemId);
  if (deletedItemIds.length === 0) return;

  const deletedItemIdSet = new Set(deletedItemIds);
  queryClient.setQueryData<StoryboardItemCacheEntry[]>(
    ['storyboard-items', payload.storyboardId],
    (current) =>
      current?.filter((item) => !deletedItemIdSet.has(item.itemId ?? item.item_id ?? '')) ?? current
  );
}

/** 远端删除参考图 / 临时视频时，就地从 canvas layout 缓存去掉该项。 */
function applyCanvasPersistentItemsDeletedCacheSync(
  queryClient: QueryClient,
  payload: CanvasEntitiesChangedPayload
): void {
  const deletedKeys = new Set(
    payload.changedNodes
      .filter(
        (node) => node.operation === 'deleted' && isPersistentCanvasLayoutItemType(node.itemType)
      )
      .map((node) => getCanvasLayoutItemIdentityKey(node))
  );
  if (deletedKeys.size === 0) return;

  const projectIds = Array.from(
    new Set([payload.projectId, ...(payload.affectedProjectIds ?? [])].filter(Boolean))
  );

  for (const projectId of projectIds) {
    const queryKey = canvasLayoutKeys.context(
      projectId,
      payload.contextType,
      payload.assetType,
      payload.assetId,
      payload.storyboardId
    );
    queryClient.setQueryData<CanvasLayoutResponse>(queryKey, (current) => {
      if (!current?.items) return current;
      const nextItems = current.items.filter(
        (item) => !deletedKeys.has(getCanvasLayoutItemIdentityKey(item))
      );
      if (nextItems.length === current.items.length) return current;
      return { ...current, items: nextItems };
    });
  }
}

async function refreshStoryboardDialoguesCache(
  queryClient: QueryClient,
  storyboardId: string
): Promise<void> {
  const dialogues = await storyboardResourcesApi.getStoryboardDialogues(storyboardId);
  queryClient.setQueryData(
    ['storyboardDialogues', storyboardId],
    dialogues.map(mapStoryboardDialogueApiItem).sort((a, b) => a.sequenceOrder - b.sequenceOrder)
  );
}

function applyStoryboardDialogueUpsertCacheSync(
  queryClient: QueryClient,
  payload: ProjectCachePayload
): void {
  if (!payload.storyboardId || !payload.data) return;
  const dialogue = mapStoryboardDialogueApiItem(payload.data as never);
  queryClient.setQueryData(
    ['storyboardDialogues', payload.storyboardId],
    (current: ReturnType<typeof mapStoryboardDialogueApiItem>[] | undefined) => {
      const existing = current ?? [];
      const next = existing.some((item) => item.dialogueId === dialogue.dialogueId)
        ? existing.map((item) => (item.dialogueId === dialogue.dialogueId ? dialogue : item))
        : [...existing, dialogue];
      return next.sort((a, b) => a.sequenceOrder - b.sequenceOrder);
    }
  );
}

function invalidateStoryboardCanvasEntityRelatedQueries(
  queryClient: QueryClient,
  payload: CanvasEntitiesChangedPayload
): void {
  const storyboardId = payload.storyboardId?.trim();
  if (!storyboardId) return;

  const changedItemTypes = new Set(payload.changedNodes.map((node) => String(node.itemType)));

  if (changedItemTypes.has('storyboard_item')) {
    void invalidateEpisodeLinkedResourceQueries(queryClient);
    void queryClient.invalidateQueries({ queryKey: ['storyboard-items', storyboardId] });
    void queryClient.refetchQueries({
      queryKey: ['storyboard-items', storyboardId],
      type: 'active',
    });
  }

  if (changedItemTypes.has('storyboard_plan_image')) {
    void queryClient.invalidateQueries({ queryKey: ['storyboard-images', storyboardId] });
    void queryClient.invalidateQueries({
      queryKey: ['storyboard-plans', 'storyboard', storyboardId, 1],
    });
  }

  if (changedItemTypes.has('storyboard_plan_video')) {
    void queryClient.invalidateQueries({ queryKey: ['storyboard-videos', storyboardId] });
    void queryClient.invalidateQueries({
      queryKey: ['storyboard-plans', 'storyboard', storyboardId, 2],
    });
  }
}

function invalidateCanvasEntitiesChangedQueries(
  queryClient: QueryClient,
  payload: CanvasEntitiesChangedPayload
): void {
  applyStoryboardItemDeletedCacheSync(queryClient, payload);
  applyCanvasPersistentItemsDeletedCacheSync(queryClient, payload);
  invalidateCanvasSnapshotQueries(queryClient, payload);

  const projectIds = Array.from(
    new Set([payload.projectId, ...(payload.affectedProjectIds ?? [])])
  );

  for (const projectId of projectIds) {
    const queryKey = canvasLayoutKeys.context(
      projectId,
      payload.contextType,
      payload.assetType,
      payload.assetId,
      payload.storyboardId
    );
    void queryClient.invalidateQueries({
      queryKey,
    });
    void queryClient.refetchQueries({
      queryKey,
      type: 'active',
    });
  }

  if (payload.contextType === 'storyboard') {
    invalidateStoryboardCanvasEntityRelatedQueries(queryClient, payload);
    return;
  }

  invalidateProjectResourceAndTagQueries(queryClient, {
    projectId: payload.projectId,
    affectedProjectIds: projectIds,
  });
}

function invalidateCanvasSnapshotQueries(
  queryClient: QueryClient,
  payload: {
    projectId?: string | null;
    affectedProjectIds?: string[];
    contextType?: CanvasContextType | null;
    storyboardId?: string | null;
    assetType?: CanvasAssetType | null;
    assetId?: string | null;
  }
): void {
  const projectIds = Array.from(
    new Set(
      [payload.projectId, ...(payload.affectedProjectIds ?? [])].filter(
        (projectId): projectId is string => Boolean(projectId)
      )
    )
  );
  for (const projectId of projectIds) {
    if (payload.storyboardId) {
      void queryClient.invalidateQueries({
        queryKey: canvasSnapshotKeys.context({
          projectId,
          contextType: 'storyboard',
          storyboardId: payload.storyboardId,
        }),
      });
      // history 挂在 context 前缀下；显式再刷一次，避免展开历史后生成列表不更新。
      void queryClient.invalidateQueries({
        queryKey: [
          ...canvasSnapshotKeys.context({
            projectId,
            contextType: 'storyboard',
            storyboardId: payload.storyboardId,
          }),
          'history',
        ],
      });
      continue;
    }
    if (payload.contextType === 'asset' && payload.assetType && payload.assetId) {
      void queryClient.invalidateQueries({
        queryKey: canvasSnapshotKeys.context({
          projectId,
          contextType: 'asset',
          assetType: payload.assetType,
          assetId: payload.assetId,
        }),
      });
      void queryClient.invalidateQueries({
        queryKey: [
          ...canvasSnapshotKeys.context({
            projectId,
            contextType: 'asset',
            assetType: payload.assetType,
            assetId: payload.assetId,
          }),
          'history',
        ],
      });
      continue;
    }
    void queryClient.invalidateQueries({
      queryKey: [...canvasSnapshotKeys.all, projectId],
    });
  }
}

/**
 * 实时重连（resync_required）后重取画布首屏 snapshot。
 *
 * 仅将 snapshot 查询标记为过期并以 refetchType: 'active' 只重取当前正在展示的画布 context，
 * 避免宽泛失效导致其它项目/上下文缓存被清空或触发多余请求；有 projectId 时进一步按项目收窄。
 */
function resyncCanvasSnapshotQueries(queryClient: QueryClient, payload: ProjectCachePayload): void {
  const projectIds = getPayloadProjectIds(payload);
  if (projectIds.length === 0) {
    void queryClient.invalidateQueries({
      queryKey: canvasSnapshotKeys.all,
      refetchType: 'active',
    });
    return;
  }
  for (const projectId of projectIds) {
    void queryClient.invalidateQueries({
      queryKey: [...canvasSnapshotKeys.all, projectId],
      refetchType: 'active',
    });
  }
}

function invalidateCanvasCustomGroupQueries(
  queryClient: QueryClient,
  payload: ProjectCachePayload
): void {
  const projectIds = getPayloadProjectIds(payload);
  if (projectIds.length === 0) {
    void queryClient.invalidateQueries({ queryKey: canvasCustomGroupsKeys.all });
    return;
  }
  for (const projectId of projectIds) {
    void queryClient.invalidateQueries({
      queryKey: [...canvasCustomGroupsKeys.all, projectId],
    });
  }
}

const PROJECT_IMMEDIATE_STORYBOARD_MEDIA_EVENTS = new Set([
  'project_media_changed',
  'storyboard_sound_effect_changed',
  'storyboard_voiceover_changed',
  'storyboard_video_changed',
]);

function isImmediateStoryboardMediaPayload(payload: ProjectCachePayload): boolean {
  return (
    payload.entityType === 'sound_effect' ||
    payload.entityType === 'dialogue_voiceover' ||
    payload.entityType === 'storyboard_dialogue' ||
    payload.entityType === 'storyboard_video'
  );
}

function invalidateStoryboardImageMediaQueries(
  queryClient: QueryClient,
  payload: ProjectCachePayload
): void {
  const storyboardId = payload.storyboardId?.trim();
  if (!storyboardId) return;

  void queryClient.invalidateQueries({ queryKey: ['storyboard-images', storyboardId] });
  void queryClient.invalidateQueries({
    queryKey: ['storyboard-plans', 'storyboard', storyboardId, 1],
  });

  const projectIds = getPayloadProjectIds(payload);
  for (const projectId of projectIds) {
    void queryClient.invalidateQueries({
      queryKey: ['storyboard-plans', 'project', projectId],
    });
    void queryClient.invalidateQueries({
      queryKey: [
        ...canvasSnapshotKeys.context({
          projectId,
          contextType: 'storyboard',
          storyboardId,
        }),
        'history',
      ],
    });
  }
}

function applyImmediateStoryboardMediaCacheEvent(
  queryClient: QueryClient,
  payload: ProjectCachePayload
): void {
  if (payload.changeType === 'deleted' && payload.storyboardId && payload.entityId) {
    if (payload.entityType === 'sound_effect') {
      applyStoryboardSoundEffectDeletedCacheSync(queryClient, {
        storyboardId: payload.storyboardId,
        entityId: payload.entityId,
      });
    } else if (payload.entityType === 'dialogue_voiceover') {
      applyStoryboardVoiceoverDeletedCacheSync(queryClient, {
        storyboardId: payload.storyboardId,
        entityId: payload.entityId,
      });
    } else if (payload.entityType === 'storyboard_video') {
      applyStoryboardVideoDeletedCacheSync(queryClient, {
        storyboardId: payload.storyboardId,
        entityId: payload.entityId,
      });
    } else if (payload.entityType === 'storyboard_dialogue') {
      applyStoryboardDialogueDeletedCacheSync(queryClient, {
        storyboardId: payload.storyboardId,
        entityId: payload.entityId,
      });
    }
  }

  if (payload.entityType === 'storyboard_video') {
    void refreshStoryboardVideoCollaborationCache(queryClient, {
      storyboardId: payload.storyboardId,
      projectId: payload.projectId,
      parentProjectId: payload.parentProjectId,
      affectedProjectIds: payload.affectedProjectIds,
    });
  }

  if (payload.data && (payload.changeType === 'created' || payload.changeType === 'updated')) {
    if (payload.entityType === 'dialogue_voiceover') {
      upsertStoryboardVoiceoverCache(
        queryClient,
        payload.data as unknown as DialogueVoiceoverResponse
      );
    } else if (payload.entityType === 'sound_effect') {
      upsertStoryboardSoundEffectCache(
        queryClient,
        payload.data as unknown as StoryboardSoundEffectResponse
      );
    }
  }

  invalidateProjectProductionQueries(queryClient, payload);
  if (payload.entityType === 'storyboard_dialogue' && payload.storyboardId) {
    if (payload.changeType === 'created' || payload.changeType === 'updated') {
      applyStoryboardDialogueUpsertCacheSync(queryClient, payload);
    }
    void refreshStoryboardDialoguesCache(queryClient, payload.storyboardId);
  }
}

const PROJECT_RESOURCE_DELETE_EVENTS = new Set([
  'project_resource_deleted',
  'project_element_deleted',
]);

const PROJECT_RESOURCE_AND_TAG_EVENTS = new Set([
  'project_resource_created',
  'project_resource_updated',
  'project_resource_deleted',
  'project_element_created',
  'project_element_updated',
  'project_element_deleted',
  'project_element_tag_created',
  'project_element_tag_updated',
  'project_element_tag_deleted',
  'project_element_tag_selected_resource_changed',
  'project_element_tag_cover_image_changed',
  'project_element_default_tag_changed',
]);

const PROJECT_STATE_EVENTS = new Set([
  'project_created',
  'project_meta_changed',
  'project_state_changed',
]);

const PROJECT_STRUCTURE_EVENTS = new Set(['project_structure_changed']);

const EPISODE_RESOURCE_LINK_EVENTS = new Set([
  'project_screenplay_changed',
  'project_storyboard_changed',
  'screenplay_deleted',
  'screenplay_parse_result_changed',
  'storyboard_created',
  'storyboard_deleted',
  'storyboard_batch_saved',
  'storyboard_item_changed',
]);

const PROJECT_PRODUCTION_EVENTS = new Set([
  'project_subproject_created',
  'project_screenplay_changed',
  'project_storyboard_changed',
  'project_media_changed',
  'project_storyboard_review_changed',
  'project_ai_job_changed',
  'screenplay_created',
  'screenplay_updated',
  'screenplay_deleted',
  'screenplay_reordered',
  'screenplay_parse_requested',
  'screenplay_parse_result_changed',
  'storyboard_created',
  'storyboard_updated',
  'storyboard_deleted',
  'storyboard_reordered',
  'storyboard_batch_saved',
  'storyboard_item_changed',
  'storyboard_review_changed',
  'storyboard_media_changed',
  'storyboard_dialogue_changed',
  'storyboard_voiceover_changed',
  'storyboard_sound_effect_changed',
  'storyboard_video_changed',
  'ai_task_changed',
]);

function deriveCanvasEntityHighlightsFromCacheEvent(
  event: RealtimeEvent,
  context: RealtimeContext,
  canvasPayload: CanvasEntitiesChangedPayload
): void {
  if (resolveRouteHighlightContext(context.pathname) !== 'canvas') return;
  if (!isRealtimeCollaborationProject(context.currentProject)) return;

  const actorUserId = readString(event.payload.actorUserId);
  const currentUserId = useAppStore.getState().user?.userId;
  if (actorUserId && currentUserId && actorUserId === currentUserId) return;

  const highlights = mapCanvasEntitiesChangedToHighlights({
    projectId: canvasPayload.projectId,
    storyboardId: canvasPayload.storyboardId,
    contextType: canvasPayload.contextType,
    changedNodes: canvasPayload.changedNodes,
    actorUserId,
    occurredAt: readString(event.payload.occurredAt),
  });

  const store = useCollaborationHighlightStore.getState();
  for (const highlight of highlights) {
    store.addHighlight(highlight);
  }
}

export function handleProjectCacheRealtimeEvent(
  queryClient: QueryClient,
  event: RealtimeEvent,
  context: RealtimeContext
): void {
  const payload = readProjectCachePayload(event.payload);

  if (event.event === 'resync_required') {
    void queryClient.invalidateQueries({ queryKey: ['screenplay-workflow-snapshot'] });
    flushCacheBuckets('project_cache:');
    invalidateProjectStateQueries(queryClient, payload);
    invalidateProjectResourceAndTagQueries(queryClient, payload);
    invalidateProjectProductionQueries(queryClient, payload);
    resyncCanvasSnapshotQueries(queryClient, payload);
    invalidateCanvasCustomGroupQueries(queryClient, payload);
    return;
  }

  if (event.event === 'screenplay_workflow_changed') {
    const screenplayId = readString(event.payload.screenplayId);
    const actorUserId = readString(event.payload.actorUserId);
    // 本人的命令与 SSE 已有统一刷新；协作者仅重取权威缓存，不发起动作。
    if (!screenplayId || actorUserId === useAppStore.getState().user?.userId) return;
    scheduleWorkflowCacheRefresh(queryClient, `project_cache:workflow:${screenplayId}`, [
      ['screenplay-workflow-snapshot', screenplayId],
    ]);
    const sessionIds = Array.isArray(event.payload.sessionIds) ? event.payload.sessionIds : [];
    for (const sessionId of new Set(sessionIds)) {
      if (typeof sessionId !== 'string') continue;
      scheduleWorkflowCacheRefresh(
        queryClient,
        `project_cache:ai-session:${sessionId}`,
        ['session', 'messages', 'active-runs'].map((kind) => ['ai-session', kind, sessionId])
      );
    }
    return;
  }

  if (event.event === 'screenplay_coordination_changed') {
    const screenplayId = readString(event.payload.screenplayId);
    const version = event.payload.ownershipVersion;
    if (!screenplayId || typeof version !== 'number' || !Number.isInteger(version)) return;
    const filters = {
      queryKey: ['screenplay-workflow-snapshot', screenplayId],
      predicate: (query: Query) => {
        const snapshot = query.state.data as ScreenplayWorkflowSnapshot | undefined;
        return (snapshot?.coordination?.ownershipVersion ?? -1) < version;
      },
    };
    // 取消旧快照后重取；本地响应已更新的版本不再重复查询。
    void queryClient.cancelQueries(filters).then(() => queryClient.invalidateQueries(filters));
    return;
  }

  if (event.event === 'generation_reference_draft_changed') {
    const draft = readGenerationReferenceDraftChangedPayload(event.payload);
    if (draft) applyGenerationReferenceDraftChanged(queryClient, draft);
    return;
  }

  if (event.event === 'canvas_conversation_changed') {
    const conversation = readCanvasConversationChangedPayload(event.payload);
    if (!conversation) return;
    const actorUserId = readString(event.payload.actorUserId);
    const currentUserId = useAppStore.getState().user?.userId ?? null;
    if (
      !isOwnCanvasConversationChange({
        actorUserId,
        conversationUserId: conversation.conversation.userId,
        currentUserId,
      })
    ) {
      removeCanvasSnapshotConversationById(queryClient, {
        projectId: conversation.projectId,
        conversationId: conversation.conversation.conversationId,
      });
      return;
    }
    applyCanvasConversationChanged(queryClient, conversation);
    return;
  }

  if (event.event === 'project_access_revoked') {
    applyProjectAccessRevokedEffects(queryClient, payload, context);
    return;
  }

  if (event.event === 'project_auth_changed') {
    applyProjectAuthChangedEffects(queryClient, payload, context, {
      suppressToast: true,
    });
    return;
  }

  if (event.event === 'project_budget_changed') {
    scheduleProjectCacheBucket('project_cache:budget', event.event, payload, (mergedPayload) =>
      invalidateProjectBudgetChangedQueries(queryClient, mergedPayload)
    );
    return;
  }

  if (event.event === 'project_org_collaboration_changed') {
    scheduleProjectCacheBucket('project_cache:pco', event.event, payload, (mergedPayload) => {
      invalidateProjectOrgCollaborationQueries(queryClient, mergedPayload);
      applyProjectAuthChangedEffects(queryClient, mergedPayload, context, {
        suppressToast: true,
      });
    });
    return;
  }

  if (PROJECT_RESOURCE_AND_TAG_EVENTS.has(event.event)) {
    const imageEditHistoryOnly = Boolean(
      payload.imageEditHistoryOnly && payload.projectId && payload.elementId && payload.elementType
    );
    if (imageEditHistoryOnly) {
      invalidateCanvasHistoryQueriesForAsset(queryClient, {
        projectId: payload.projectId!,
        assetId: payload.elementId!,
        assetType: mapElementTypeToAssetType(payload.elementType),
        includeSnapshot: false,
      });
    } else {
      invalidateCanvasSnapshotQueries(queryClient, payload);
    }
    if (event.event === 'project_resource_deleted') {
      applyProjectResourceDeletedCacheSync(queryClient, payload);
    } else if (event.event === 'project_element_deleted') {
      applyProjectElementDeletedCacheSync(queryClient, payload);
    }

    if (PROJECT_RESOURCE_DELETE_EVENTS.has(event.event)) {
      invalidateProjectResourceAndTagQueries(queryClient, payload);
      return;
    }

    // footage 重命名：先就地无感更新可见面（即时无闪烁），随后仍走原有失活兜底，
    // 保证描述/封面/定稿等其它更新以及就地未覆盖的关联缓存不受影响。
    if (event.event === 'project_resource_updated') {
      applyProjectResourceUpdatedCacheSync(queryClient, {
        resourceId: payload.resourceId,
        resourceType: payload.resourceType,
        elementType: payload.elementType,
        name: payload.resourceName,
      });
    }

    // 标签定稿变更：先就地对齐关联节点 resourceId / 标签 selectedResourceId，再 debounce 失活重拉封面等。
    if (event.event === 'project_element_tag_selected_resource_changed') {
      const syncResult = applyTagSelectedResourceChangedCacheSync(
        queryClient,
        payload,
        imageEditHistoryOnly ? { includeSnapshot: false } : undefined
      );
      const tagId = payload.tagId?.trim();
      const skipAttention = tagId
        ? useCanvasAssetAssociationAttentionStore.getState().consumeLocalTagChange(tagId)
        : false;
      if (!skipAttention && syncResult.changedStoryboardItemIds.length > 0) {
        useCanvasAssetAssociationAttentionStore
          .getState()
          .markItems(syncResult.changedStoryboardItemIds);
      }
    }

    scheduleCacheBucket('project_cache:resources', event.event, () => {
      const mergedPayload = pendingResourceAndTagPayload ?? payload;
      pendingResourceAndTagPayload = null;
      invalidateProjectResourceAndTagQueries(queryClient, mergedPayload);
    });
    pendingResourceAndTagPayload = mergeResourceAndTagPayload(
      pendingResourceAndTagPayload,
      payload
    );
    return;
  }

  if (PROJECT_STATE_EVENTS.has(event.event)) {
    scheduleProjectCacheBucket('project_cache:state', event.event, payload, (mergedPayload) =>
      invalidateProjectStateQueries(queryClient, mergedPayload)
    );
    return;
  }

  if (PROJECT_STRUCTURE_EVENTS.has(event.event)) {
    scheduleProjectCacheBucket('project_cache:structure', event.event, payload, (mergedPayload) =>
      invalidateProjectStructureQueries(queryClient, mergedPayload)
    );
    return;
  }

  if (PROJECT_IMMEDIATE_STORYBOARD_MEDIA_EVENTS.has(event.event)) {
    const payload = readProjectCachePayload(event.payload);
    if (event.event === 'project_media_changed' && payload.entityType === 'storyboard_image') {
      invalidateCanvasSnapshotQueries(queryClient, payload);
      invalidateStoryboardImageMediaQueries(queryClient, payload);
      return;
    }
    if (isImmediateStoryboardMediaPayload(payload)) {
      invalidateCanvasSnapshotQueries(queryClient, payload);
      applyImmediateStoryboardMediaCacheEvent(queryClient, payload);
      return;
    }
  }

  if (
    event.event === 'project_storyboard_changed' &&
    payload.changeType &&
    STORYBOARD_ITEM_ASSOCIATION_CHANGE_TYPES.has(payload.changeType)
  ) {
    invalidateStoryboardItemAssociationQueries(queryClient, payload);
    return;
  }

  if (event.event === 'canvas_custom_groups_changed') {
    invalidateCanvasCustomGroupQueries(queryClient, payload);
    return;
  }

  if (event.event === 'canvas_entities_changed') {
    const canvasPayload = readCanvasEntitiesChangedPayload(event.payload);
    if (canvasPayload) {
      const scope =
        canvasPayload.contextType === 'storyboard'
          ? `storyboard:${canvasPayload.storyboardId ?? 'all'}`
          : `asset:${canvasPayload.assetType ?? 'x'}:${canvasPayload.assetId ?? 'all'}`;
      scheduleCanvasEntitiesChangedBucket(
        `project_cache:canvas_entities:${canvasPayload.projectId}:${scope}`,
        event.event,
        canvasPayload,
        (mergedPayload) => invalidateCanvasEntitiesChangedQueries(queryClient, mergedPayload)
      );
      deriveCanvasEntityHighlightsFromCacheEvent(event, context, canvasPayload);
    }
    return;
  }

  if (PROJECT_PRODUCTION_EVENTS.has(event.event)) {
    if (EPISODE_RESOURCE_LINK_EVENTS.has(event.event)) {
      void invalidateEpisodeLinkedResourceQueries(queryClient);
    }
    invalidateCanvasSnapshotQueries(queryClient, payload);
    scheduleProjectCacheBucket('project_cache:production', event.event, payload, (mergedPayload) =>
      invalidateProjectProductionQueries(queryClient, mergedPayload)
    );
  }
}

export function handleOrgCacheRealtimeEvent(
  queryClient: QueryClient,
  event: RealtimeEvent,
  context: RealtimeContext
): void {
  if (event.event === 'resync_required') {
    flushCacheBuckets('org_cache:');
    invalidateContextDependentQueries(queryClient);
    return;
  }

  if (event.event !== 'org_access_changed') {
    if (event.event === 'org_member_changed') {
      const payload = readOrgCachePayload(event.payload);
      scheduleCacheBucket(`org_cache:members:${payload.orgId ?? 'all'}`, event.event, () => {
        if (payload.orgId) {
          void queryClient.invalidateQueries({ queryKey: orgMembersKeys.byOrg(payload.orgId) });
        } else {
          void queryClient.invalidateQueries({ queryKey: ['org-members'] });
        }
      });
    }
    return;
  }

  const payload = readOrgCachePayload(event.payload);
  if (!shouldApplyOrgAccessChanged(payload)) return;
  void applyOrgAccessChangedEffects(queryClient, payload, context);
}
